import React, { useCallback, useRef, useState } from "react";
import { useOnboardingStore, StepIds, StepType } from "../../../stores/onboardingStore";
import { SudoButton } from "../../../sudokit/components/SudoButton";
import { Textarea } from "../../../sudokit/components/Textarea";
import { SudoTooltip } from "../../../sudokit/components/SudoTooltip";
import { sendAppEvent } from "../../../Utils";
import InlineSVG from "react-inlinesvg";
import Auth from "../../../Auth";
import { useSudowriteStore } from "../../../stores/sudowriteStore";
import PluginCreationAccessGate from "../../EditPlugin/PluginCreationAccessGate";
import { useToast } from "../../ToastWrapper";

const PLUGIN_EXAMPLES = [
  "I want to take my brainstorming notes and turn them into a cohesive story outline, with suggestions for the overall arc and structure.",
  "I need to find logical inconsistencies in my text — plot holes, timeline errors, and contradictions between scenes.",
  "I need my editor to identify boring or redundant passages and suggest how to make them more engaging or what to cut entirely.",
  "My story is set in 1880s America. I need an editor who checks every detail for historical accuracy — language, technology, customs, and social norms of the era.",
  "I want to make sure all my characters stay consistent — their voice, mannerisms, motivations, and physical descriptions shouldn't contradict across chapters.",
  "I want to analyze my dialogue so each character has a distinct, recognizable voice that doesn't blend together.",
  "I need help identifying where my pacing drags or rushes, with specific suggestions for how to fix the rhythm.",
  "I want an editor that catches overused words, clichés, and weak verbs, then suggests stronger alternatives that fit my style.",
  "I need to verify my worldbuilding is internally consistent — the rules of my magic system, geography, and culture should never contradict themselves.",
  "I want feedback on my story's emotional beats — are the moments that should hit hard actually landing, or do they need more buildup?",
  "I'm writing a mystery and need an editor who checks that clues are planted fairly and the reveal is satisfying without being obvious.",
  "I want to tighten my prose — find every sentence that could be shorter without losing meaning and show me how.",
];

const PluginCreationSteps: React.FC = () => {
  const { currentStepId, answers, recordAnswer, next, back, generatedPlugin, setGeneratedPlugin } = useOnboardingStore();
  const { setToast } = useToast();
  const [generatingPlugin, setGeneratingPlugin] = useState(false);
  const [spin, setSpin] = useState(false);
  const lastExampleIndex = useRef(-1);
  const pluginCreationBlocked = useSudowriteStore(
    (state) => state.subscriptionJSON?.hasAccessToPluginCreation === false
  );

  const currentStep = useOnboardingStore((s) => {
    const stepId = s.currentStepId;
    if (!stepId || stepId === "done") return undefined;
    return s.flow.steps[stepId];
  });

  const handleGeneratePlugin = useCallback(async () => {
    const description = answers[StepIds.DescribePlugin] as string;
    if (!description || description.trim().length < 5) return;

    const isRetry = !!generatedPlugin?.shortId;
    sendAppEvent("Onboarding - Plugin Creation Started", { isRetry });

    setGeneratingPlugin(true);
    try {
      const payload: { description: string; replacePluginShortId?: string } = { description };
      if (generatedPlugin?.shortId) {
        payload.replacePluginShortId = generatedPlugin.shortId;
      }
      const result = await Auth.fetchWithIdToken("/api/ai/generateOnboardingPlugin", payload);
      setGeneratedPlugin(result.plugin);
      recordAnswer(StepIds.DescribePlugin, description);
      next();
    } catch (err: any) {
      const json = err?.response ? await err.response.json().catch(() => null) : null;
      setToast(json?.error || "Something went wrong generating your plugin. Please try again.");
    } finally {
      setGeneratingPlugin(false);
    }
  }, [answers, generatedPlugin, next, recordAnswer, setGeneratedPlugin, setToast]);

  const handleRandomExample = useCallback(() => {
    if (spin) return;
    let index = Math.floor(Math.random() * PLUGIN_EXAMPLES.length);
    if (index === lastExampleIndex.current) {
      index = (index + 1) % PLUGIN_EXAMPLES.length;
    }
    lastExampleIndex.current = index;
    recordAnswer(StepIds.DescribePlugin, PLUGIN_EXAMPLES[index]);
    setSpin(true);
    setTimeout(() => setSpin(false), 300);
  }, [spin, recordAnswer]);

  const handleSkipPluginCreation = useCallback(() => {
    sendAppEvent("Onboarding - Plugin Creation Skipped");
    recordAnswer(StepIds.DescribePlugin, "skipped");
    recordAnswer(StepIds.BraindumpNotSure, true);
    next();
  }, [next, recordAnswer]);

  if (currentStep?.type !== StepType.Custom) return null;

  if (currentStepId === StepIds.DescribePlugin) {
    if (pluginCreationBlocked) {
      return (
        <div className="w-full max-w-2xl">
          <PluginCreationAccessGate blocked />
          <div className="mt-6 flex items-center justify-center">
            <SudoButton
              variant="tertiaryGrey"
              type="button"
              aria-label="Skip plugin creation"
              tabIndex={0}
              onClick={handleSkipPluginCreation}
            >
              Skip
            </SudoButton>
          </div>
        </div>
      );
    }

    return (
      <div className="w-full max-w-2xl">
        <div className="relative">
          <Textarea
            mode="default"
            placeholder="e.g. I want feedback on pacing in my chapters, or help tightening my prose..."
            value={(answers[StepIds.DescribePlugin] as string) ?? ""}
            onChange={(e) => {
              recordAnswer(StepIds.DescribePlugin, e.target.value);
            }}
            disabled={generatingPlugin}
            className="pr-9"
          />
          {!generatingPlugin && (
            <div className="absolute right-2 top-2 z-10">
              <SudoTooltip text="Random example" position="top" hasArrow={false}>
                <button
                  type="button"
                  onClick={handleRandomExample}
                  aria-label="Random example"
                  className="flex items-center justify-center rounded-md p-1 cursor-pointer bg-transparent border-none hover:bg-app-grey-4 transition-colors duration-150"
                >
                  <InlineSVG
                    src="/assets/brainstorm/refresh-icon.svg"
                    className="w-[15px] h-[15px] [&_path]:stroke-app-grey-1 [&_path]:opacity-70 hover:[&_path]:opacity-100 [&_path]:transition-opacity [&_path]:duration-200"
                    style={spin ? { transition: "transform 300ms", transform: "rotate(180deg)" } : { transition: "transform 300ms" }}
                  />
                </button>
              </SudoTooltip>
            </div>
          )}
        </div>
        <div className="mt-6 flex items-center justify-center gap-6">
          <SudoButton
            variant="tertiaryGrey"
            type="button"
            aria-label="Skip plugin creation"
            tabIndex={0}
            onClick={handleSkipPluginCreation}
            disabled={generatingPlugin}
          >
            Skip
          </SudoButton>
          <SudoButton
            variant="primary"
            size="default"
            onClick={handleGeneratePlugin}
            disabled={generatingPlugin || !answers[StepIds.DescribePlugin] || (answers[StepIds.DescribePlugin] as string).trim().length < 5}
            className="rounded-[8px] focus-visible:ring-2 focus-visible:ring-app-primary-cta-high-contrast focus-visible:ring-offset-2"
            tabIndex={0}
          >
            {generatingPlugin ? "Crafting your plugin..." : "Create Plugin"}
          </SudoButton>
        </div>
      </div>
    );
  }

  if (currentStepId === StepIds.PluginResult && generatedPlugin) {
    return (
      <div className="w-full max-w-2xl">
        <div className="rounded-2xl border border-solid border-app-grey-3 bg-white p-6 text-left shadow-sm">
          <h3 className="font-gt-super-text text-xl font-medium text-app-grey-0 mb-2">
            {generatedPlugin.name}
          </h3>
          <p className="text-base text-app-grey-1 leading-relaxed">
            {generatedPlugin.description}
          </p>
        </div>
        <div className="mt-6 flex items-center justify-center gap-6">
          <SudoButton
            variant="tertiaryGrey"
            type="button"
            aria-label="Try again"
            tabIndex={0}
            onClick={() => {
              sendAppEvent("Onboarding - Plugin Try Again Clicked");
              back();
            }}
          >
            Try again
          </SudoButton>
          <SudoButton
            variant="primary"
            size="default"
            onClick={next}
            className="rounded-[8px] focus-visible:ring-2 focus-visible:ring-app-primary-cta-high-contrast focus-visible:ring-offset-2"
            tabIndex={0}
          >
            Continue
          </SudoButton>
        </div>
      </div>
    );
  }

  return null;
};

export default PluginCreationSteps;
